import { Result } from 'neverthrow';

import type {
  CreateLoadBuildingSuggestionRequest,
  CreateLoadBuildingSuggestionResponse,
} from 'lib/api/createLoadBuildingSuggestion';
import { buildLocalLoadBuildingSuggestionFromRequest } from 'pages/QuoteView/scrapedSuggestionUtil';
import type { ApiError } from 'types/api/ApiError';
import type { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestionSourceCategories } from 'types/suggestions/QuoteSuggestions';
import captureException from 'utils/captureException';

import { type SharepointRow, isSharepointAgileRow } from '../types/Sharepoint';
import type { MatchedSharepointDefaults } from './sharepointToLoadSuggestion';
import {
  buildSyntheticSharepointLoadBuildingSuggestion,
  matchSharepointDefaultsToTMS,
  matchSharepointRowToTMS,
  mergeSharepointSuggestedWithTMSMatches,
  sharepointLoadBuildingSourceForRow,
  sharepointPersistRequestForRow,
  sharepointRowToSuggestedLoad,
  stableSyntheticSuggestionIdForSharepointRow,
} from './sharepointToLoadSuggestion';

export function withSharepointPasteHints(
  suggestion: LoadBuildingSuggestions,
  row: SharepointRow
): LoadBuildingSuggestions {
  const withStableId: LoadBuildingSuggestions = {
    ...suggestion,
    sharepointStableSyntheticId:
      stableSyntheticSuggestionIdForSharepointRow(row),
  };
  if (isSharepointAgileRow(row)) {
    return { ...withStableId, sharepointAgilePasteHint: true };
  }
  return withStableId;
}

/**
 * Shared TMS match cache for fail-open synthetics across concurrent row workers.
 * Agile rows share one defaults search.
 */
export type SharepointTmsMatchCache = {
  matchForRow(
    tmsID: number,
    row: SharepointRow
  ): Promise<MatchedSharepointDefaults>;
};

export function createSharepointTmsMatchCache(): SharepointTmsMatchCache {
  let agileTmsMatchPromise: Promise<MatchedSharepointDefaults> | undefined;

  return {
    async matchForRow(tmsID, row) {
      if (isSharepointAgileRow(row)) {
        if (!agileTmsMatchPromise) {
          agileTmsMatchPromise = matchSharepointDefaultsToTMS(tmsID);
        }
        return agileTmsMatchPromise;
      }
      return matchSharepointRowToTMS(tmsID, row);
    },
  };
}

export type PersistLoadBuildingSuggestion = (
  request: CreateLoadBuildingSuggestionRequest
) => Promise<Result<CreateLoadBuildingSuggestionResponse, ApiError>>;

export type ProcessSharepointRowForSuggestionParams = {
  row: SharepointRow;
  worksheet: string;
  sourceURL: string;
  emailIdForCard: number;
  tmsID: number;
  tmsMatchCache: SharepointTmsMatchCache;
  persist: PersistLoadBuildingSuggestion;
};

async function buildFailOpenSyntheticCard({
  row,
  rawSuggested,
  emailIdForCard,
  tmsID,
  tmsMatchCache,
  sourceURL,
  sourceExternalID,
}: {
  row: SharepointRow;
  rawSuggested: ReturnType<typeof sharepointRowToSuggestedLoad>;
  emailIdForCard: number;
  tmsID: number;
  tmsMatchCache: SharepointTmsMatchCache;
  sourceURL: string;
  /** Omit when worksheet is unknown to avoid unstable `agile||booking` keys. */
  sourceExternalID?: string;
}): Promise<LoadBuildingSuggestions> {
  const matched = await tmsMatchCache.matchForRow(tmsID, row);
  const merged = mergeSharepointSuggestedWithTMSMatches(rawSuggested, matched);
  const synthetic = buildSyntheticSharepointLoadBuildingSuggestion(
    merged,
    emailIdForCard,
    {
      syntheticSuggestionId: stableSyntheticSuggestionIdForSharepointRow(row),
      source: sharepointLoadBuildingSourceForRow(row),
      sourceURL,
      sourceCategory: SuggestionSourceCategories.Sharepoint,
      ...(sourceExternalID != null ? { sourceExternalID } : {}),
    }
  );
  return withSharepointPasteHints(synthetic, row);
}

/**
 * Persist a SharePoint row as a load-building suggestion; on API failure or unknown
 * worksheet, fail-open to a synthetic (negative id) card enriched with TMS matches.
 *
 * Worksheet must be known before persist: an empty worksheet creates unstable source keys
 * and a second DB row once the real sheet name resolves.
 */
export async function processSharepointRowForSuggestion({
  row,
  worksheet,
  sourceURL,
  emailIdForCard,
  tmsID,
  tmsMatchCache,
  persist,
}: ProcessSharepointRowForSuggestionParams): Promise<LoadBuildingSuggestions> {
  const rawSuggested = sharepointRowToSuggestedLoad(row);
  const canPersist = worksheet.trim().length > 0;

  if (!canPersist) {
    return buildFailOpenSyntheticCard({
      row,
      rawSuggested,
      emailIdForCard,
      tmsID,
      tmsMatchCache,
      sourceURL,
    });
  }

  const persistRequest = sharepointPersistRequestForRow(row, rawSuggested, {
    worksheet,
    sourceURL,
  });
  const persistResult = await persist(persistRequest);

  if (persistResult.isOk()) {
    return withSharepointPasteHints(
      {
        ...buildLocalLoadBuildingSuggestionFromRequest(
          persistRequest,
          persistResult.value.suggestionID,
          persistResult.value.suggested
        ),
        emailID: emailIdForCard,
        latestEmailIDFromThread: emailIdForCard,
      },
      row
    );
  }

  captureException(new Error('sharepoint load-building persist fail-open'), {
    source: persistRequest.source,
    sourceExternalID: persistRequest.sourceExternalID,
    sourceCategory: persistRequest.sourceCategory,
    message: persistResult.error.message,
  });

  return buildFailOpenSyntheticCard({
    row,
    rawSuggested,
    emailIdForCard,
    tmsID,
    tmsMatchCache,
    sourceURL,
    sourceExternalID: persistRequest.sourceExternalID,
  });
}
